import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { useAuth } from '../contexts/AuthContext';

const baseUrl = "https://run.phazeflow.com";
const chains = [
  { key: 'sepolia', name: 'Sepolia Testnet' },
  { key: 'ethereum', name: 'Ethereum' },
  { key: 'bsc', name: 'BSC' }
];

export default function AdminExecutorContracts() {
  const { user, token } = useAuth();
  const [contractAddresses, setContractAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({ chainKey: '', contractType: 'DelegatedExecutor', address: '' });
  const [addressLoading, setAddressLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadContractAddresses();
    }
    // eslint-disable-next-line
  }, [user]);

  const loadContractAddresses = async () => {
    setAddressLoading(true);
    try {
      const response = await fetch(baseUrl + '/api/delegation/contract-addresses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`API error: ${response.status} - ${await response.text()}`);
      const data = await response.json();
      if (data.success) setContractAddresses(data.addresses || []);
    } catch (error) {
      setError(`Error loading contract addresses: ${error.message}`);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleSetContractAddress = async () => {
    setAddressLoading(true);
    try {
      const response = await fetch(baseUrl + '/api/delegation/contract-address', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newAddress)
      });
      if (!response.ok) throw new Error(`API error: ${response.status} - ${await response.text()}`);
      setSuccess('Contract address updated');
      setNewAddress({ chainKey: '', contractType: 'DelegatedExecutor', address: '' });
      loadContractAddresses();
    } catch (error) {
      setError(`Error updating contract address: ${error.message}`);
    } finally {
      setAddressLoading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <Alert severity="error">You do not have permission to view this page.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>Executor Contracts</Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>DelegatedExecutor Contract Addresses</Typography>
          {addressLoading ? <CircularProgress /> : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chain</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contractAddresses.map(addr => (
                    <TableRow key={addr.chain_key + addr.contract_type}>
                      <TableCell>{addr.chain_key}</TableCell>
                      <TableCell>{addr.contract_type}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace' }}>{addr.address}</TableCell>
                      <TableCell>{addr.updated_at ? new Date(addr.updated_at).toLocaleString() : ''}</TableCell>
                    </TableRow>
                  ))}
                  {contractAddresses.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center">No contract addresses set</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box display="flex" gap={2} alignItems="center" mt={2}>
            <TextField
              select
              label="Chain Key"
              value={newAddress.chainKey}
              onChange={e => setNewAddress({ ...newAddress, chainKey: e.target.value })}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {chains.map(chain => (
                <MenuItem key={chain.key} value={chain.key}>{chain.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Contract Type"
              value={newAddress.contractType}
              onChange={e => setNewAddress({ ...newAddress, contractType: e.target.value })}
              size="small"
              disabled
            />
            <TextField
              label="Address"
              value={newAddress.address}
              onChange={e => setNewAddress({ ...newAddress, address: e.target.value })}
              size="small"
              sx={{ minWidth: 300 }}
            />
            <Button variant="contained" onClick={handleSetContractAddress} disabled={addressLoading || !newAddress.chainKey || !newAddress.address}>
              Set Address
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
} 